import React, { useEffect, useState } from "react";
import api, { apiError } from "@/api";
import { CHALLENGE_METRIC } from "@/lib/constants";
import { Swords, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { playSuccess } from "@/lib/sound";

function Countdown({ end }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const diff = new Date(end) - new Date();
      if (diff <= 0) return setLeft("انتهى");
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setLeft(`${d} يوم ${h}س ${m}د ${s}ث`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [end]);
  return <span className="font-head font-bold text-amber-600 tabular-nums">{left}</span>;
}

export default function ChallengeCard({ isCoordinator }) {
  const [ch, setCh] = useState(null);
  const load = () => api.get("/challenges/active").then((r) => setCh(r.data.challenge)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!ch) return null;

  const pct = ch.target > 0 ? Math.min(100, (ch.progress / ch.target) * 100) : 0;
  const barColor = pct >= 100 ? "#10B981" : pct >= 66 ? "#D4AF37" : pct >= 33 ? "#F59E0B" : "#EF4444";

  const complete = async () => {
    try {
      const { data } = await api.post(`/challenges/${ch.id}/complete`);
      playSuccess();
      toast.success(`أحسنت! تم منح ${data.points} نقطة (×${data.multiplier})`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="challenge-card" className="glass rounded-2xl p-5 relative overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Swords className="text-amber-600" size={20} />
          <span className="font-head font-bold text-stone-800">التحدي الأسبوعي</span>
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-700">
          {ch.points} نقطة × {ch.multiplier}
        </span>
      </div>
      <h4 className="font-head font-bold text-lg text-stone-800">{ch.title}</h4>
      {ch.description && <p className="text-sm text-stone-500 mt-1">{ch.description}</p>}
      <div className="flex items-center justify-between mt-3 text-sm">
        <span className="text-stone-500">{CHALLENGE_METRIC[ch.metric]}</span>
        <Countdown end={ch.end_date} />
      </div>
      {isCoordinator && (
        <>
          <div className="mt-3">
            <div className="flex justify-between text-xs text-stone-500 mb-1">
              <span>{ch.progress} / {ch.target}</span>
              <span>{Math.round(pct)}%</span>
            </div>
            <div className="h-3 rounded-full bg-amber-100 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: barColor }} />
            </div>
          </div>
          {ch.completed ? (
            <div className="mt-3 flex items-center justify-center gap-2 py-2 rounded-xl bg-emerald-500/15 border border-emerald-400/40 text-emerald-700 font-semibold">
              <CheckCircle2 size={18} /> أنجزت التحدي — +{ch.earned_points} نقطة
            </div>
          ) : pct >= 100 ? (
            <button data-testid="complete-challenge-btn" onClick={complete} className="btn-gold w-full mt-3 py-2 rounded-xl">
              تأكيد إنجاز التحدي
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
